// domain/filter-clause.ts — контракт фильтров и сортировки (SSOT для клиента и сервера).

import { z } from "zod";

export const FILTER_TYPES = [
  "text",
  "number-range",
  "date-range",
  "select-single",
  "select-multi",
  "boolean",
  "empty",
  "not-empty",
] as const;
export type TFilterType = (typeof FILTER_TYPES)[number];
export const FILTER_TYPE_OBJ = {
  TEXT: FILTER_TYPES[0],
  NUMBER_RANGE: FILTER_TYPES[1],
  DATE_RANGE: FILTER_TYPES[2],
  SELECT_SINGLE: FILTER_TYPES[3],
  SELECT_MULTI: FILTER_TYPES[4],
  BOOLEAN: FILTER_TYPES[5],
  EMPTY: FILTER_TYPES[6],
  NOT_EMPTY: FILTER_TYPES[7],
} as const satisfies Record<string, TFilterType>;

export const TEXT_OPERATORS = [
  "contains",
  "equals",
  "starts",
  "ends",
  "not-contains",
  "not-equals",
] as const;
export type TextOperator = (typeof TEXT_OPERATORS)[number];
export const TEXT_OPERATOR_OBJ = {
  CONTAINS: TEXT_OPERATORS[0],
  EQUALS: TEXT_OPERATORS[1],
  STARTS: TEXT_OPERATORS[2],
  ENDS: TEXT_OPERATORS[3],
  NOT_CONTAINS: TEXT_OPERATORS[4],
  NOT_EQUALS: TEXT_OPERATORS[5],
} as const satisfies Record<string, TextOperator>;

/**
 * Kind of value stored in a column. Determines how `empty`/`not-empty` filters
 * are built (SQL and client-side) and how NULL semantics work for negation.
 *
 * - `text` / `enum`: empty = `IS NULL OR ::text = ''`; not-empty = `IS NOT NULL AND ::text != ''`
 * - `number` / `date` / `boolean`: empty = `IS NULL`; not-empty = `IS NOT NULL`
 *   (typed columns cannot hold an empty string)
 */
export const COLUMN_VALUE_KINDS = ["text", "enum", "number", "date", "boolean"] as const;
export type ColumnValueKind = (typeof COLUMN_VALUE_KINDS)[number];
export const COLUMN_VALUE_KIND_OBJ = {
  TEXT: COLUMN_VALUE_KINDS[0],
  ENUM: COLUMN_VALUE_KINDS[1],
  NUMBER: COLUMN_VALUE_KINDS[2],
  DATE: COLUMN_VALUE_KINDS[3],
  BOOLEAN: COLUMN_VALUE_KINDS[4],
} as const satisfies Record<string, ColumnValueKind>;

export const filterClauseSchema = z.discriminatedUnion("type", [
  z.object({
    column: z.string().min(1),
    type: z.literal("text"),
    value: z.string(),
    operator: z.enum(TEXT_OPERATORS).optional(),
    negate: z.boolean().optional(),
  }),
  z.object({
    column: z.string().min(1),
    type: z.literal("number-range"),
    min: z.number().optional(),
    max: z.number().optional(),
    negate: z.boolean().optional(),
  }),
  z.object({
    column: z.string().min(1),
    type: z.literal("date-range"),
    from: z.string().optional(),
    to: z.string().optional(),
    negate: z.boolean().optional(),
  }),
  z.object({
    column: z.string().min(1),
    type: z.literal("select-single"),
    value: z.string(),
    negate: z.boolean().optional(),
  }),
  z.object({
    column: z.string().min(1),
    type: z.literal("select-multi"),
    values: z.array(z.string()),
    negate: z.boolean().optional(),
  }),
  z.object({
    column: z.string().min(1),
    type: z.literal("boolean"),
    value: z.boolean(),
    negate: z.boolean().optional(),
  }),
  z.object({
    column: z.string().min(1),
    type: z.literal("empty"),
    negate: z.boolean().optional(),
  }),
  z.object({
    column: z.string().min(1),
    type: z.literal("not-empty"),
    negate: z.boolean().optional(),
  }),
]);
export type FilterClause = z.infer<typeof filterClauseSchema>;

export const filterGroupSchema = z.object({
  type: z.literal("or"),
  clauses: z.array(filterClauseSchema),
});
export type FilterGroup = z.infer<typeof filterGroupSchema>;

export const SORT_DIRECTIONS = ["asc", "desc"] as const;
export type TSortDirection = (typeof SORT_DIRECTIONS)[number];
export const SORT_DIRECTION_OBJ = {
  ASC: SORT_DIRECTIONS[0],
  DESC: SORT_DIRECTIONS[1],
} as const satisfies Record<string, TSortDirection>;

export const sortClauseSchema = z.object({
  column: z.string().min(1),
  direction: z.enum(SORT_DIRECTIONS),
});
export type SortClause = z.infer<typeof sortClauseSchema>;

